// src/Pages/Auth/SignIn.jsx
// CHANGES FROM PREVIOUS VERSION:
//   - Removed BetaMessage import and both usages (replaced by Founders Circle)
//   - Added Founders Circle link as first link in both desktop and mobile link blocks
//   - ALL auth code, useEffects, Google signin, CSS — UNTOUCHED

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { signInWithRedirect } from '@aws-amplify/auth';
import { I18n } from '@aws-amplify/core';
import { Helmet } from 'react-helmet-async';
import EnhancedSEO from '../../Components/SEO/EnhancedSEO';
import {
  Box, Container, Typography, Button, Card, CardContent, Divider,
  Fade, Zoom, useTheme, useMediaQuery, Chip, Stack, Grid, Link,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Security as SecurityIcon,
  ArrowForward as ArrowIcon,
  ArrowRightAlt as ArrowFlowIcon,
  SubdirectoryArrowRight as ArrowDownIcon,
  RateReview as ReviewIcon,
  Share as SocialIcon,
  WbSunny as WeatherIcon,
  QuestionAnswer as ChatIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import './Styles.css';
import { resolveNarrative } from './variants';
import { getLandingContext } from '../../lib/landingContext';


// ─── SEO — unchanged ──────────────────────────────────────────────────────────

const faqSchema = {
  "@context": "https://schema.org", "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "What is Revfer?",
      "acceptedAnswer": { "@type": "Answer", "text": "Revfer is an agentic AI platform for businesses under five employees. It watches a business's real signals — new reviews, local weather and events, seasonal shifts, repeated customer questions — and starts the work on its own: review replies drafted and published directly to Google Business Profile on approval, social posts written and scheduled to Facebook, Instagram and LinkedIn, and recurring questions turned into published FAQs answered by a 24/7 chatbot. There is no prompt to write and no dashboard to operate. The owner reads and approves." } },
    { "@type": "Question", "name": "Do I have to write prompts or operate a dashboard?",
      "acceptedAnswer": { "@type": "Answer", "text": "No. Unlike general-purpose AI tools that start from an empty prompt box, or review-management dashboards built for multi-location teams, Revfer initiates the work from signals it detects and presents finished drafts for approval — roughly 10 minutes a week in place of about ten hours." } },
    { "@type": "Question", "name": "Does Revfer post my review replies to Google?",
      "acceptedAnswer": { "@type": "Answer", "text": "Yes. Approved replies publish directly to Google Business Profile through a connected account. Yelp and additional platforms are next." } },
    { "@type": "Question", "name": "How much does Revfer cost?",
      "acceptedAnswer": { "@type": "Answer", "text": "Free tier available. Premium is $19/month at launch pricing, $49/month after launch. No credit card to start." } },
    { "@type": "Question", "name": "How long does setup take?",
      "acceptedAnswer": { "@type": "Answer", "text": "About 15 minutes from signup to fully operational." } },
  ],
};

const webAppSchema = {
  "@context": "https://schema.org", "@type": "WebApplication",
  "name": "Revfer — AI that starts your customer engagement work, not waits for a prompt",
  "url": "https://www.revfer.com",
  "applicationCategory": "BusinessApplication",
  "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
};
// Narrative content moved to ./variants/ — see variants/index.js for the
// registry and resolver.


// ─── Main component ────────────────────────────────────────────────────────────

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme    = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const locationState = location.state || {};
  const nextPage      = locationState;
  const forceRefresh  = locationState.forceRefresh || false;

  const { authStatus, error } = useAuthenticator((c) => [c.authStatus, c.error]);

  // Which narrative this visitor sees. Resolved once on mount — localStorage is
  // already written by the redirect component before it navigates here.
  const Narrative = React.useMemo(() => resolveNarrative(getLandingContext()), []);
  const [isPostSignout, setIsPostSignout] = useState(false);
  const [key,           setKey]           = useState(`auth-init`);
  const [showContent,   setShowContent]   = useState(false);
  const [isSubmitting,  setIsSubmitting]  = useState(false);

  // ── ALL useEffect HOOKS — COMPLETELY UNCHANGED ─────────────────────────────
  useEffect(() => { const t = setTimeout(() => setShowContent(true), 100); return () => clearTimeout(t); }, []);

  useEffect(() => {
    if (forceRefresh && !isPostSignout) {
      setIsPostSignout(true);
      setTimeout(() => { setKey(`auth-fresh-${Date.now()}`); window.history.replaceState({}, document.title); }, 200);
    }
  }, [forceRefresh, isPostSignout]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setKey(`auth-error-${Date.now()}`), 300);
      return () => clearTimeout(t);
    }
  }, [error]);

  useEffect(() => {
    I18n.putVocabularies({ en: { 'Sign in': 'Sign In', 'Sign in to your account': 'Sign In', 'Send code': 'Send Code' } });
  }, []);

  useEffect(() => {
    if (authStatus === 'authenticated') {
      navigate('/home', { state: { link: nextPage?.link ?? null, onboarding: true } });
    }
  }, [authStatus, navigate, nextPage]);

  useEffect(() => {
    const handleAmplifySubmit = (e) => {
      const btn = e.target.closest('button[type="submit"], button.amplify-button--primary');
      if (btn) setIsSubmitting(true);
    };
    document.addEventListener('click', handleAmplifySubmit, true);
    return () => document.removeEventListener('click', handleAmplifySubmit, true);
  }, []);

  useEffect(() => {
    if (authStatus !== 'configuring') setIsSubmitting(false);
  }, [authStatus]);

  useEffect(() => {
    if (error) setIsSubmitting(false);
  }, [error]);

  const handleSignOut     = (so) => { so(); setIsPostSignout(true); setTimeout(() => setKey(`local-signout-${Date.now()}`), 200); };
  const handleGoogleLogin = () => { try { signInWithRedirect({ provider: 'Google' }); } catch (e) { console.error(e); } };

  // ── Authenticated fallback — UNCHANGED ─────────────────────────────────────
  if (authStatus === 'authenticated') {
    return (
      <>
        <EnhancedSEO title="Welcome Back — Revfer Dashboard" description="Access your AI-powered business tools." />
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #F3F9FF 0%, #E8F4FD 100%)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Authenticator loginMechanisms={['email']} socialProviders={['google']} signUpAttributes={['family_name', 'given_name']} key={key}>
            {({ signOut, user }) => (
              <Card sx={{ p: 4, maxWidth: 400, width: '100%', textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom color="primary">Welcome Back!</Typography>
                <Typography variant="h6" gutterBottom>{user?.attributes?.name || user?.username}</Typography>
                <Button variant="outlined" color="error" onClick={() => handleSignOut(signOut)} sx={{ mt: 2 }}>Sign out</Button>
              </Card>
            )}
          </Authenticator>
        </Box>
      </>
    );
  }

  // ── Unauthenticated ────────────────────────────────────────────────────────
  return (
    <>
      <EnhancedSEO
        title="Revfer — AI That Does the Work, Not Waits for a Prompt"
        description="Revfer watches your reviews, your season and your customers' questions, then drafts the reply, writes the post and answers the question — before you ask. Replies publish straight to Google on approval. Built for businesses under five employees. $19/mo at launch. Free to start."
        keywords="agentic AI small business, automatic review response Google Business Profile, AI review reply posting, social media autopilot small business, zero prompt AI marketing, review management alternative"
        type="website" image="/og-image.jpg"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(webAppSchema)}</script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #F3F9FF 0%, #E8F4FD 100%)', position: 'relative', overflowX: 'clip' }}>
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(circle at 15% 85%, rgba(144,202,249,0.12) 0%, transparent 45%), radial-gradient(circle at 85% 15%, rgba(197,202,233,0.12) 0%, transparent 45%)' }} />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: 2 }}>
          <Grid container sx={{ minHeight: '100vh', alignItems: 'flex-start', py: 4 }}>

            {/* ═══════════════════════════════════════════════════════════
                DESKTOP LEFT PANEL — narrative only, lg+ only
            ══════════════════════════════════════════════════════════════ */}
            {isDesktop && (
              <Grid item lg={5}>
                <Fade in={showContent} timeout={1000}>
                  <Box>
                    <Narrative compact={false} ctx={getLandingContext()} />
                  </Box>
                </Fade>
              </Grid>
            )}

            {/* ═══════════════════════════════════════════════════════════
                RIGHT PANEL — mobile strip + auth card
            ══════════════════════════════════════════════════════════════ */}
            <Grid item xs={12} lg={7} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: 650 }}>

                {/* ── MOBILE NARRATIVE STRIP ─────────────────────────────── */}
                {!isDesktop && (
                  <Fade in={showContent} timeout={800}>
                    <Box sx={{ width: '100%' }}>
                      <Narrative compact={true} ctx={getLandingContext()} />
                    </Box>
                  </Fade>
                )}

                {/* ════════════════════════════════════════════════════════
                    AUTH CARD — COMPLETELY UNTOUCHED
                    Authenticator, CSS, handlers, key remounting,
                    Google signin, isSubmitting overlay — all unchanged.
                ═════════════════════════════════════════════════════════ */}
                <Zoom in={showContent} timeout={800}>
                  <Card sx={{
                    width: '100%', maxWidth: 650, background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: 3, boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    overflow: 'visible', display: 'flex', flexDirection: 'column', mb: 1, zIndex: 1,
                  }}>
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', minHeight: 'auto' }}>

                      <style jsx global>{`
                        .amplify-authenticator,.amplify-authenticator__router,.amplify-authenticator__form,.amplify-card,[data-amplify-authenticator],[data-amplify-authenticator]>div,[data-amplify-authenticator] .amplify-card,[data-amplify-authenticator] .amplify-authenticator__router{background-color:white!important;background:white!important;border:none!important;border-radius:24px!important;padding:0!important;margin:0!important;box-shadow:none!important;outline:none!important;overflow:visible!important;min-height:auto!important;height:auto!important}
                        .amplify-authenticator *:not(button):not(input),[data-amplify-authenticator] *:not(button):not(input){border:none!important;outline:none!important;box-shadow:none!important}
                        .amplify-button--primary{background-color:#0288D1!important;border-color:#0288D1!important;min-height:44px!important;font-size:14px!important;border:1px solid #0288D1!important;margin-bottom:8px!important}
                        .amplify-button--primary:hover{background-color:#0277BD!important}
                        .amplify-input{border-radius:6px!important;border:1px solid #dadce0!important;min-height:44px!important;font-size:14px!important;background-color:white!important;margin-bottom:4px!important;pointer-events:auto!important;user-select:text!important;-webkit-user-select:text!important;cursor:text!important}
                        .amplify-input:focus{border-color:#0288D1!important;box-shadow:0 0 0 2px rgba(2,136,209,0.2)!important}
                        .amplify-field,.amplify-field-group,.amplify-field-group__field-wrapper{pointer-events:auto!important}
                        [data-amplify-authenticator] input,[data-amplify-authenticator] input:disabled{pointer-events:auto!important;opacity:1!important;cursor:text!important}
                        .amplify-field-group__control button,.amplify-field__show-password,[data-amplify-field-group-control] button{border:1px solid #dadce0!important;border-radius:6px!important;background-color:white!important;width:40px!important;height:44px!important;display:flex!important;align-items:center!important;justify-content:center!important;transition:all 0.2s ease!important;position:absolute!important;right:0px!important;top:50%!important;transform:translateY(calc(-50% - 2px))!important;margin:0!important}
                        .amplify-field-group__control button:hover,.amplify-field__show-password:hover,[data-amplify-field-group-control] button:hover{border-color:#0288D1!important;background-color:rgba(2,136,209,0.04)!important;transform:translateY(calc(-50% - 2px)) scale(1.05)!important}
                        .amplify-field-group,.amplify-field-group__field-wrapper{position:relative!important}
                        .amplify-field{margin-bottom:12px!important;border:none!important;position:relative!important}
                        .amplify-field__label{font-size:14px!important;font-weight:500!important;margin-bottom:4px!important;color:#333!important}
                        .amplify-tabs [role="tablist"]{margin-bottom:16px!important;background-color:white!important;border:none!important}
                        .amplify-tabs [role="tab"]{font-size:14px!important;padding:8px 16px!important;background-color:white!important;color:#333!important;border:none!important}
                        .amplify-tabs [role="tab"][aria-selected="true"]{background-color:white!important;border-bottom:2px solid #0288D1!important;border-top:none!important;border-left:none!important;border-right:none!important}
                        .amplify-text{color:#333!important}.amplify-link{color:#0288D1!important}
                        .custom-google-button,.custom-or-divider{border:2px solid #dadce0!important}
                        .custom-google-button:hover{border-color:#0288D1!important}
                        .amplify-authenticator{border:none!important;border-radius:24px!important;box-shadow:none!important;margin:0!important;padding:0!important;overflow:visible!important;background:white!important;width:100%!important;min-height:auto!important;height:auto!important;display:block!important}
                        [data-amplify-authenticator]{position:static!important;border:none!important;border-radius:24px!important;overflow:visible!important;box-shadow:none!important;background:white!important;width:100%!important;margin:0!important;padding:0!important;min-height:auto!important;height:auto!important}
                        [data-amplify-authenticator][data-variation="modal"]{position:static!important;inset:auto!important;top:auto!important;left:auto!important;right:auto!important;bottom:auto!important;width:100%!important;height:auto!important;min-height:auto!important;max-height:none!important;background-color:transparent!important;overflow:visible!important;padding:0!important;display:block!important;z-index:auto!important;align-items:normal!important;justify-content:normal!important}
                        [data-amplify-authenticator][data-variation="modal"]>[data-amplify-router]{width:100%!important;max-width:none!important;box-shadow:none!important;border:none!important;margin:0!important}
                        [data-amplify-authenticator]>div,[data-amplify-authenticator] [data-amplify-container]{width:100%!important;max-width:100%!important;min-width:0!important;background:transparent!important;background-color:transparent!important;border-radius:0!important;box-shadow:none!important;border:none!important}
                        .amplify-authenticator__form,.amplify-authenticator__router,.amplify-card{border:none!important;outline:none!important;box-shadow:none!important;margin:0!important;padding:0!important;min-height:auto!important;height:auto!important;overflow:visible!important}
                      `}</style>

                                

                   

                      <Box sx={{ position: 'relative', minHeight: 'auto', display: 'flex', flexDirection: 'column', '& > *': { backgroundColor: 'transparent !important', border: 'none !important', boxShadow: 'none !important' } }}>
                        <Authenticator loginMechanisms={['email']} socialProviders={['google']} signUpAttributes={['family_name', 'given_name']} key={key} variation="modal" hideSignUp={false}>
                          {() => null}
                        </Authenticator>

                        {isSubmitting && (
                          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, bgcolor: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(2px)', borderRadius: 2, zIndex: 10 }}>
                            <Box sx={{ width: 28, height: 28, borderRadius: '50%', border: '3px solid #E3F2FD', borderTopColor: '#0288D1', animation: 'revfer-spin 0.7s linear infinite', '@keyframes revfer-spin': { to: { transform: 'rotate(360deg)' } } }} />
                            <Typography variant="caption" sx={{ color: '#0288D1', fontWeight: 600, fontSize: 13 }}>Please wait…</Typography>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, opacity: 0.7 }}>
                        <SecurityIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">Secure and encrypted connection</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default SignIn;